/**
 * Utility for detecting device capabilities and types
 */
import { useState, useEffect } from 'react';

/**
 * Checks if the current device is a mobile device
 *
 * @returns boolean - true if the device is mobile
 */
export const isMobileDevice = (): boolean => {
  // Check if we're on the client side
  if (typeof window === 'undefined') return false;

  // Check for touch capability
  const hasTouchCapability =
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    ((navigator as any).msMaxTouchPoints || 0) > 0;

  // Check for mobile user agent patterns
  const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  // Check screen size (common mobile breakpoint)
  const hasSmallScreen = window.innerWidth <= 768;

  // Consider it a mobile device if it has touch capability AND either a mobile user agent OR a small screen
  return hasTouchCapability && (isMobileUserAgent || hasSmallScreen);
};

/**
 * Checks if the device is an iOS device
 *
 * @returns boolean - true if the device is running iOS
 */
export const isIOSDevice = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

/**
 * Checks if the device is an Android device
 *
 * @returns boolean - true if the device is running Android
 */
export const isAndroidDevice = (): boolean => {
  return /Android/i.test(navigator.userAgent);
};

/**
 * React hook to get device information and respond to window size changes
 *
 * @returns Object containing device information
 */

export const useDeviceInfo = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: isMobileDevice(),
    isIOS: isIOSDevice(),
    isAndroid: isAndroidDevice(),
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    screenHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    // Update on resize
    const handleResize = () => {
      setDeviceInfo({
        isMobile: isMobileDevice(),
        isIOS: isIOSDevice(),
        isAndroid: isAndroidDevice(),
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
      });
    };

    // Set initial values
    handleResize();

    // Listen for window resize events
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return deviceInfo;
};

export default {
  isMobileDevice,
  isIOSDevice,
  isAndroidDevice,
  useDeviceInfo,
};
